import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { theme, Text, Row, Space, Icon, Image, Spinner } from 'ui'
import { Button, Radio, Checkbox, IconButton,  Select, InputLabel, MenuItem } from '@material-ui/core'
import { Edit, Delete, Add, Close, ChevronLeft, ChevronRight } from '@material-ui/icons'
import { DateTimePicker, MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'

import { TextField } from '@material-ui/core'
import { observer } from 'mobx-react'
import useStore from 'store'

import { 
    DialogHeader, 
    Header, 
    Body, 
    StyledClose,
    StyledIconButton,
    CategoryListTitleContainer,
    CategoryListTitleSubContainer1,
    CategoryListTitleSubContainer2,
    HeaderTitle1,
    HeaderTitle2,
    ImageHeader, 
    AddCategoryButton,
    CategoryList,
    CategoryListItem,
    CategoryListItemContainer,
    CategoryListItemActions,
    CategoryListItemContent,
    CategoryItemImageIcon,
    CategoryListItemTitle1,
    NewCatHeading3,
    NewCategoryBody,
    NewCategoryContainer,
    NewSubCategoryContainer,
    NewCatHeading4,
    NewCatHeading5,
    SearchContainer,
    NewCategoryContainerColumn,
    AllUsersCheckboxContainer,
    NewCategoryContainerActions,
    CategoryListItemIcon,
    CategoryListItemSubContent,
    CategoryListItemTitle2,
    CategoryListUserContent,
    CategoryListUserItem,
    CategoryListUserItem1,
    CategoryListUserItem2,
     
} from './CategoryModal.styles'

import { FormInput, FormSelect, ColorPicker, FormSearch, UserOption } from './FormComponents'
import { Category } from './api'
import { getProtocolPermissions } from './permissions'
import RemoveCategoryModal from './RemoveCategoryModal'

import SettingsIcon from 'assets/icons/ims/Settings.svg'
import EditIcon from 'assets/icons/ims/Edit2.svg'
import DeleteIcon from 'assets/icons/ims/Delete.svg'
import Icon1 from 'assets/icons/protocols/icon1.svg'
import Icon2 from 'assets/icons/protocols/icon2.svg'
import Icon3 from 'assets/icons/protocols/icon3.svg'
import Icon9 from 'assets/icons/protocols/icon9.svg'
import Icon10 from 'assets/icons/protocols/icon10.svg'



const SaveButton = styled(Button)`
  display: flex;
  padding: 8.724px 23.631px 7.261px 23.992px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #155DFC;
  color: #FFF;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;  
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
`
const CancelButton = styled(Button)`
  display: flex;
  padding: 9.634px 23.98px 8.17px 24.901px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
`

const CategoryModal = ({ open, onCloseModal }) => {
  const { members, orgs, uiStore, memberStore, user, modulesStore, protocolsStore } = useStore()
  const { t } = useTranslation('members')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null)
  const [categoryName, setCategoryName] = useState('')
  const [categoryDescription, setCategoryDescription] = useState('')
  const [categoryIcon, setCategoryIcon] = useState('Users')
  const [categoryColor, setCategoryColor] = useState('#3B82F6')
  const categoryColors = ['#64748B', '#EC4899', '#06B6D4', '#EF4444', '#F59E0B', '#8B5CF6', '#10B981', '#3B82F6']
  const [categoryManagers, setCategoryManagers] = useState<UserOption[]>([])
  const [categoryApprovers, setCategoryApprovers] = useState<UserOption[]>([])
  const [categoryViewers, setCategoryViewers] = useState<UserOption[]>([])
  const [allUsersCanView, setAllUsersCanView] = useState(false)
  const categoryIconOptions = [
    { value: 'Users',      label: 'Users' },
    { value: 'Building',   label: 'Building' },
    { value: 'Scale',      label: 'Scale' },
    { value: 'DollarSign', label: 'DollarSign' },
    { value: 'FileText',   label: 'FileText' },
    { value: 'Briefcase',  label: 'Briefcase' },
    { value: 'Home',       label: 'Home' },
    { value: 'Calendar',   label: 'Calendar' },
  ]
  
  const perms = getProtocolPermissions(protocolsStore.userPermissions)
  const categoryList = protocolsStore.categories

  useEffect(() => {
    if (!open) return

    if (protocolsStore.baseUrl == "") {
      protocolsStore.init(orgs.currentOrgId).then(data => {
        protocolsStore.loadCategories(orgs.currentOrgId)
        protocolsStore.loadUserList()
      })
    } else {
      protocolsStore.loadCategories(orgs.currentOrgId)
      protocolsStore.loadUserList()
    }
    
  }, [open])

  const resetForm = () => {
    setEditingCategory(null)
    setCategoryName('')
    setCategoryDescription('')
    setCategoryColor('#3B82F6')
    setCategoryIcon('Users')
    setCategoryManagers([])
    setCategoryApprovers([])
    setCategoryViewers([])
    setAllUsersCanView(false)
  }

  const openEdit = (item: Category) => {
    setEditingCategory(item)
    setCategoryName(item.categoryName)
    setCategoryDescription(item.description || '')
    setCategoryColor(categoryColors[(item.colorId ?? 1) - 1] || '#3B82F6')
    setCategoryIcon(categoryIconOptions[(item.iconId ?? 1) - 1]?.value || 'Users')
    setCategoryManagers(item.categoryManagers.map(m => ({ userId: m.userId, fullName: m.fullName })))
    setCategoryApprovers(item.approvingManagers.map(m => ({ userId: m.userId, fullName: m.fullName })))
    setCategoryViewers(item.viewers.map(m => ({ userId: m.userId, fullName: m.fullName })))
    setAllUsersCanView(item.forAllCommunityUsers)
    setShowNewCategory(true)
  }

  const handleSave = async () => {
    if (!categoryName.trim()) return
    setSaving(true)
    setError(null)
    const colorId = categoryColors.indexOf(categoryColor) + 1
    const iconId = categoryIconOptions.findIndex(o => o.value === categoryIcon) + 1
    const body = {
      categoryName: categoryName.trim(),
      description: categoryDescription.trim(),
      colorId,
      iconId,
      forAllCommunityUsers: allUsersCanView,
      categoryManagers: categoryManagers.map(u => ({ userId: u.userId, memberRoleId: 1 })),
      approvingManagers: categoryApprovers.map(u => ({ userId: u.userId, memberRoleId: 2 })),
      viewers: categoryViewers.map(u => ({ userId: u.userId, memberRoleId: 3 })),
    }
    const res = editingCategory
      ? await protocolsStore.updateCategory(orgs.currentOrgId, editingCategory.id, body)
      : await protocolsStore.createCategory(orgs.currentOrgId, body)
    setSaving(false)
    if (res) {
      resetForm()
      setShowNewCategory(false)
    }
  }   
  
  return (
    <>
        <RemoveCategoryModal
          open={!!deletingCategory}
          closeModal={() => setDeletingCategory(null)}
          categoryId={deletingCategory?.id}
          categoryName={deletingCategory?.categoryName}
        />
        <Dialog
          style={{ zIndex: 1200 }} 
          open={open} 
          fullWidth 
          maxWidth={'lg'}
          scroll={'body'}
        >
          <DialogHeader>
            <Header>
                <CategoryListTitleContainer>
                    <CategoryListTitleSubContainer2>
                        <ImageHeader src={SettingsIcon} />
                    </CategoryListTitleSubContainer2>
                    <CategoryListTitleSubContainer1>
                        <HeaderTitle1>ניהול קטגוריות</HeaderTitle1>
                        <HeaderTitle2>הגדר קטגוריות והרשאות גישה</HeaderTitle2>
                    </CategoryListTitleSubContainer1>
                </CategoryListTitleContainer>
                <StyledIconButton onClick={() => onCloseModal(false)}><Close style={{ color: '#364153' }} /></StyledIconButton>
            </Header>

          </DialogHeader>
          <DialogContent>
            {!showNewCategory && <Body>
                {perms.canCreateCategory && (
                  <AddCategoryButton onClick={() => setShowNewCategory(true)}><Add/> צור קטגוריה חדשה</AddCategoryButton>
                )}
                <CategoryList>
                {categoryList.map(item =>
                  <CategoryListItem>
                    <CategoryListItemContainer>
                        <CategoryListItemContent>
                          <CategoryListItemIcon bg={categoryColors[(item.colorId ?? 1) - 1]}>
                          </CategoryListItemIcon>
                          <CategoryListItemSubContent>
                            <CategoryListItemTitle1>{item.categoryName}</CategoryListItemTitle1>
                            <CategoryListItemTitle2>{item.description}</CategoryListItemTitle2>
                            <CategoryListUserContent>
                              <CategoryListUserItem>
                                <CategoryListUserItem1><Image src={Icon9}/>מנהלי קטגוריה</CategoryListUserItem1>
                                <CategoryListUserItem2>{item.categoryManagers.length} משתמשים</CategoryListUserItem2>
                              </CategoryListUserItem>
                              <CategoryListUserItem>
                                <CategoryListUserItem1><Image src={Icon10}/>מאשרים</CategoryListUserItem1>
                                <CategoryListUserItem2>{item.approvingManagers.length} משתמשים</CategoryListUserItem2>
                              </CategoryListUserItem>
                            </CategoryListUserContent>
                          </CategoryListItemSubContent>
                        </CategoryListItemContent>
                        <CategoryListItemActions>
                            {perms.canEditCategoryById(item.id) && (
                              <StyledIconButton onClick={() => openEdit(item)}><CategoryItemImageIcon src={EditIcon}/></StyledIconButton>
                            )}
                            {perms.canDeleteCategory && (
                              <StyledIconButton onClick={() => setDeletingCategory(item)}><CategoryItemImageIcon src={DeleteIcon}/></StyledIconButton>
                            )}
                        </CategoryListItemActions>
                    </CategoryListItemContainer>
                  </CategoryListItem>
                )}
                </CategoryList>
            </Body>}
            {showNewCategory && 
              <NewCategoryBody>
                <NewCatHeading3>{editingCategory ? 'עריכת קטגוריה' : 'קטגוריה חדשה'}</NewCatHeading3>
                <NewCategoryContainer>
                  <NewSubCategoryContainer>
                    <FormInput
                      label={`שם הקטגוריה`}
                      required={true}
                      value={categoryName}
                      onChange={setCategoryName}
                      placeholder="לדוגמה: סיכומי אסיפות"
                    />
                  </NewSubCategoryContainer>
                  <NewSubCategoryContainer>
                     <FormInput
                      label={`תיאור`}
                      required={false}
                      value={categoryDescription}
                      onChange={setCategoryDescription}
                      placeholder="תיאור קצר של הקטגוריה"
                    />
                  </NewSubCategoryContainer>
                </NewCategoryContainer>
                <NewCategoryContainer>
                  <NewSubCategoryContainer>
                    <ColorPicker
                      label="צבע"
                      value={categoryColor}
                      onChange={setCategoryColor}
                      colors={categoryColors}
                    />
                  </NewSubCategoryContainer>
                  <NewSubCategoryContainer>
                    <FormSelect
                      label={'אייקון'}
                      required={false}
                      value={categoryIcon}
                      onChange={setCategoryIcon}
                      options={categoryIconOptions}
                    />
                  </NewSubCategoryContainer>
                </NewCategoryContainer>
                <NewCatHeading4>הרשאות גישה</NewCatHeading4>
                  <NewCategoryContainerColumn>
                    <NewCatHeading5><Image src={Icon1}/>מנהלי קטגוריה (יכולים ליצור ולערוך מסמכים) </NewCatHeading5>
                    <SearchContainer>
                      <FormSearch
                        label="הוספת מנהלי קטגוריה"
                        value={categoryManagers}
                        onChange={setCategoryManagers}
                        options={protocolsStore.userList}
                        placeholder="חפש לפי שם..."
                        tagColor={`#1447E6`}
                        tagBg={`#DBEAFE`}
                      />
                    </SearchContainer>
                  </NewCategoryContainerColumn>
                  <NewCategoryContainerColumn>
                    <NewCatHeading5><Image src={Icon2}/>מאשרים (יכולים לאשר מסמכים) </NewCatHeading5>
                    <SearchContainer>
                      <FormSearch
                        label="מאשרים (יכולים לאשר מסמכים)"
                        value={categoryApprovers}
                        onChange={setCategoryApprovers}
                        options={protocolsStore.userList}
                        placeholder="חפש משתמש להוספה..."
                        tagColor={`#008236`}
                        tagBg={`#DCFCE7`}
                      />
                    </SearchContainer>
                  </NewCategoryContainerColumn>
                   <NewCategoryContainerColumn>
                    <NewCatHeading5><Image src={Icon3}/>צופים (יכולים לצפות במסמכים) </NewCatHeading5>
                    <AllUsersCheckboxContainer>
                      <Checkbox
                      style={{ fontSize: '14px', color: '#8B5CF6' }}
                        checked={allUsersCanView}
                        onChange={e => setAllUsersCanView(e.target.checked)}
                        color="primary"
                      />
                      כולם - כל חברי הקהילה יכולים לצפות
                    </AllUsersCheckboxContainer>
                    {!allUsersCanView && <SearchContainer>
                      <FormSearch
                        label="צופים (יכולים לצפות במסמכים)"
                        value={categoryViewers}
                        onChange={setCategoryViewers}
                        options={protocolsStore.userList}
                        placeholder="חפש משתמש להוספה..."
                        tagColor={`#59168B`}
                        tagBg={`#FAF5FF`}
                      />
                    </SearchContainer>}
                  </NewCategoryContainerColumn>
                <NewCategoryContainerActions>
                  <CancelButton onClick={() => { resetForm(); setShowNewCategory(false) }}>ביטול</CancelButton>
                  <SaveButton onClick={handleSave} disabled={saving || !categoryName.trim()}>
                    {saving ? <Spinner size={16} /> : editingCategory ? 'שמור שינויים' : 'צור קטגוריה'}
                  </SaveButton>
                </NewCategoryContainerActions>
              </NewCategoryBody>}
            </DialogContent>
        </Dialog>
    </>
  )
}

export default observer(CategoryModal)


